import { useMemo, useState } from "react";
import { colorCategories, swatches, type Swatch } from "@/mocks/colors";
import SwatchModal from "./SwatchModal";

type Props = {
  activeCategory: string;
  setActiveCategory: (id: string) => void;
};

function SwatchCard({ item, onOpen }: { item: Swatch; onOpen: (s: Swatch) => void }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className="group cursor-pointer rounded-lg overflow-hidden border border-background-200 bg-background-50 text-left hover:-translate-y-0.5 hover:border-primary-300 transition-all"
    >
      <div className="relative h-28 md:h-32" style={{ backgroundColor: item.hex }}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_28%_18%,rgba(255,255,255,0.22),transparent_55%)]" />
        <span className="absolute top-2.5 left-3 font-number text-background-50/85 text-[11px] tracking-[0.2em]">
          {item.code}
        </span>
        <span className="absolute bottom-2.5 left-3 font-number text-background-50/70 text-[10px]">{item.hex}</span>
      </div>
      <div className="px-3.5 py-3">
        <div className="flex items-center justify-between">
          <p className="text-foreground-950 font-medium text-sm">{item.name}</p>
          <span className="text-[11px] text-foreground-400 group-hover:text-accent-600 transition-colors">{item.sheen}</span>
        </div>
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${item.tone === "冷调" ? "bg-secondary-100 text-secondary-900" : item.tone === "暖调" ? "bg-accent-100 text-accent-900" : "bg-background-200 text-foreground-700"}`}>
            {item.tone}
          </span>
          <span className="text-[10px] text-foreground-500">明度 {item.lightness}%</span>
        </div>
      </div>
    </button>
  );
}

export default function Gallery({ activeCategory, setActiveCategory }: Props) {
  const [query, setQuery] = useState("");
  const [favs, setFavs] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Swatch | null>(null);

  const catNames = useMemo(() => {
    const map: Record<string, string> = {};
    colorCategories.forEach((c) => (map[c.id] = c.name));
    return map;
  }, []);

  const filtered = useMemo(() => {
    const kw = query.trim().toLowerCase();
    return swatches.filter((s) => {
      const inCat = activeCategory === "all" || s.categoryId === activeCategory;
      if (!inCat) return false;
      if (!kw) return true;
      return (
        s.code.toLowerCase().includes(kw) ||
        s.name.includes(query.trim()) ||
        s.hex.toLowerCase().includes(kw) ||
        (catNames[s.categoryId] || "").includes(query.trim()) ||
        s.tags.some((t) => t.includes(query.trim()))
      );
    });
  }, [activeCategory, query, catNames]);

  const toggleFav = (id: string) => {
    setFavs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <section id="atlas" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <p className="flex items-center gap-3 text-accent-700 text-xs tracking-display uppercase">
              <span className="font-number text-lg">02</span>
              <span className="inline-block w-8 h-px bg-accent-500" />
              色卡图鉴
            </p>
            <h2 className="mt-4 font-serif text-foreground-950 text-2xl md:text-[32px] font-semibold">
              黑料色合集 · 按色号浏览
            </h2>
            <p className="mt-3 text-[15px] text-foreground-600 max-w-2xl">
              鼠标悬停或点击任一色块即可查看该黑料色的完整色号、明度、光泽与适用场景，HEX / RGB / CMYK
              均可一键复制。
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-sm text-foreground-600 bg-background-100 border border-background-200 px-3 py-1.5 rounded-full">
              <i className="ri-heart-3-fill text-accent-500" />
              已收藏 <span className="font-medium text-foreground-900">{favs.size}</span>
            </span>
          </div>
        </div>

        {/* 筛选工具条 */}
        <div className="mt-8 flex flex-col xl:flex-row xl:items-center gap-4 xl:justify-between">
          <div className="inline-flex rounded-full bg-background-100 border border-background-200 p-1 overflow-x-auto max-w-full">
            {[{ id: "all", name: "全部色号" }, ...colorCategories].map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setActiveCategory(c.id)}
                className={`whitespace-nowrap cursor-pointer rounded-full px-4 py-1.5 text-sm transition-colors ${
                  activeCategory === c.id
                    ? "bg-primary-900 text-background-50"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="relative w-full xl:w-80">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索色号 / 名称 / HEX / 标签"
              className="w-full rounded-full border border-background-300 bg-background-100 pl-10 pr-9 py-2.5 text-sm text-foreground-900 outline-none focus:border-accent-400 focus:bg-background-50 transition-colors"
              aria-label="搜索色卡"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="cursor-pointer absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-900"
                aria-label="清空搜索"
              >
                <i className="ri-close-circle-fill text-lg" />
              </button>
            )}
          </div>
        </div>

        {/* 结果统计 */}
        <div className="mt-6 flex items-center justify-between text-sm text-foreground-500">
          <span>
            共 <span className="text-foreground-900 font-medium">{filtered.length}</span> 个{activeCategory === "all" ? "精选" : ""}色号
            {query.trim() && <> · 关键词「<span className="text-accent-700">{query.trim()}</span>」</>}
          </span>
          <span className="hidden sm:inline text-xs text-foreground-400">提示：点击色块可查看完整色号详情</span>
        </div>

        {/* 色卡网格 */}
        {filtered.length > 0 ? (
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4">
            {filtered.map((item) => (
              <div key={item.id} className="relative group">
                <SwatchCard item={item} onOpen={setSelected} />
                <button
                  type="button"
                  onClick={() => toggleFav(item.id)}
                  className={`absolute top-2.5 right-2.5 cursor-pointer w-7 h-7 flex items-center justify-center rounded-full backdrop-blur transition-colors ${
                    favs.has(item.id)
                      ? "bg-accent-500 text-background-50"
                      : "bg-foreground-950/25 text-background-50 hover:bg-foreground-950/45"
                  }`}
                  aria-label={favs.has(item.id) ? `取消收藏${item.name}` : `收藏${item.name}`}
                >
                  <i className={`${favs.has(item.id) ? "ri-heart-3-fill" : "ri-heart-3-line"} text-sm`} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <i className="ri-inbox-2-line text-3xl text-foreground-300" />
            <p className="mt-3 text-foreground-600">没有匹配「{query.trim()}」的黑料色，换个关键词或切换色系试试。</p>
          </div>
        )}

        <p className="mt-8 text-center text-xs text-foreground-400">
          图鉴按底调与明度归档，跨色系查找请使用下方「跨系色号对照表」快速定位。
        </p>
      </div>

      {selected && (
        <SwatchModal
          swatch={selected}
          categoryName={catNames[selected.categoryId] || selected.categoryId}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  );
}